import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as L from 'leaflet';

@Component({
  selector: 'app-parcel-map',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div #mapContainer class="map-container"></div>
  `,
  styles: [`
    .map-container {
      width: 100%;
      height: 350px;
      border-radius: 4px;
      border: 1px solid #dee2e6;
    }
  `]
})
export class ParcelMapComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input() dealerLat: number | null | undefined;
  @Input() dealerLng: number | null | undefined;
  @Input() parcelLat: number | null | undefined;
  @Input() parcelLng: number | null | undefined;
  @Input() parcelCode?: string | null;
  @Input() dealerName?: string | null;
  @Input() parcelBrandCode?: string | null;
  @Input() maximumRange: number = 0;   // in metri

  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef<HTMLDivElement>;

  private map: L.Map | null = null;

  ngAfterViewInit(): void {
    // aspetto un attimo per essere sicuro che il layout sia pronto
    setTimeout(() => this.initializeMap(true), 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.map && (changes['dealerLat'] || changes['dealerLng'] || changes['parcelLat'] || changes['parcelLng'] || changes['maximumRange'])) {
      setTimeout(() => this.initializeMap(true), 100);
    }
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  private getBrandImageUrl(): string {
    if (!this.parcelBrandCode) {
      return 'assets/img/marker-custom.png';
    }
    return `assets/img/${this.parcelBrandCode}.png`;
  }

  private initializeMap(forceRecreate: boolean = false): void {
    if (!this.mapContainer) return;

    const container = this.mapContainer.nativeElement;

    // se il container è nascosto (tab non ancora visibile), rimando l’inizializzazione
    if (container.clientWidth === 0 || container.clientHeight === 0) {
      setTimeout(() => this.initializeMap(forceRecreate), 200);
      return;
    }

    const dealerLat = this.dealerLat;
    const dealerLng = this.dealerLng;

    if (dealerLat == null || dealerLng == null) {
      console.warn('ParcelMapComponent: dealer coordinates missing');
      return;
    }

    if (this.map && forceRecreate) {
      this.map.remove();
      this.map = null;
    }

    const waybillLat = this.parcelLat;
    const waybillLng = this.parcelLng;

    if (!this.map) {
      let centerLat = dealerLat;
      let centerLng = dealerLng;
      let zoomLevel = 16;

      if (waybillLat != null && waybillLng != null) {
        centerLat = (dealerLat + waybillLat) / 2;
        centerLng = (dealerLng + waybillLng) / 2;
        zoomLevel = 15;
      }

      this.map = L.map(container, {
        center: [centerLat, centerLng],
        zoom: zoomLevel,
        zoomControl: true,
        attributionControl: false
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(this.map);
    } else {
      this.map.setView([dealerLat, dealerLng], 16);
    }

    // Pulisco tutti i layer tranne la tile layer
    this.map.eachLayer(layer => {
      // @ts-ignore
      if (!layer.getAttribution) {
        this.map?.removeLayer(layer);
      }
    });

    // ====== MARKER DEALER (logo brand) ======
    const brandImageUrl = this.getBrandImageUrl();

    const dealerIcon = L.icon({
      iconUrl: brandImageUrl,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    });

    const dealerMarker = L.marker([dealerLat, dealerLng], { icon: dealerIcon }).addTo(this.map);

    const dealerPopupHtml = `
      <div class="map-popup">
        <strong>Destinazione (Dealer)</strong>
        ${this.dealerName || 'Dealer'}<br>
        <small>Lat: ${dealerLat}, Lng: ${dealerLng}</small>
      </div>
    `;
    dealerMarker.bindPopup(dealerPopupHtml);

    // ====== CERCHIO RANGE ======
    const effectiveRange = this.maximumRange && this.maximumRange > 0 ? this.maximumRange : 1000; // default 1000m

    const circle = L.circle([dealerLat, dealerLng], {
      color: '#007bff',
      fillColor: '#007bff',
      fillOpacity: 0.1,
      radius: effectiveRange
    }).addTo(this.map);
    circle.bindPopup(`Raggio di consegna: ${effectiveRange}m`);

    // ====== MARKER COLLO ======
    if (waybillLat != null && waybillLng != null) {
      const waybillIcon = L.icon({
        iconUrl: 'assets/img/marker-custom.png',
        iconSize: [28, 28],
        iconAnchor: [14, 28],
        popupAnchor: [0, -28],
        className: 'waybill-marker'
      });

      const waybillMarker = L.marker([waybillLat, waybillLng], { icon: waybillIcon }).addTo(this.map);

      const waybillPopupHtml = `
        <div class="map-popup">
          <strong>Posizione Consegna Collo</strong>
          Collo: ${this.parcelCode || ''}<br>
          <small>Lat: ${waybillLat}, Lng: ${waybillLng}</small>
        </div>
      `;
      waybillMarker.bindPopup(waybillPopupHtml).openPopup();
    }

    // ====== FIT BOUNDS ======
    const radiusInDegrees = effectiveRange / 111320; // ~111.320m per grado
    const bounds = L.latLngBounds(
      [dealerLat - radiusInDegrees, dealerLng - radiusInDegrees],
      [dealerLat + radiusInDegrees, dealerLng + radiusInDegrees]
    );

    if (waybillLat != null && waybillLng != null) {
      bounds.extend([waybillLat, waybillLng]);
    }

    this.map.fitBounds(bounds, { padding: [50, 50] });

    setTimeout(() => {
      this.map?.invalidateSize();
    }, 100);
  }
}
